/**
 * FarmListModal Component
 * Displays a list of farms from drill-down data or pre-loaded farm list
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Checkbox,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDrillDownData } from "@/api/hooks/analytics/useAnalytics";
import type { FarmSummaryDto } from "@/api/types/dtos/analytics/AnalyticsDto";
import { PRIVILEGES } from "@/api/types/dtos";
import { useHasPrivilege } from "@/components/authorization";
import { useReportOrgSelection } from "@/hooks/useReportOrgSelection";
import ReportRequestDialog from "@/components/reports/ReportRequestDialog";
import FarmDetailsModal from "@/components/modal/FarmDetailsModal";
import { formatDrillDownFilterTitle } from "../utils/drillDownTitle";

interface FarmListModalProps {
  open: boolean;
  graphId?: string;
  filterValue?: string;
  farms?: FarmSummaryDto[];
  title?: string;
  onClose: () => void;
}

export const FarmListModal: React.FC<FarmListModalProps> = ({
  open,
  graphId,
  filterValue,
  farms: preloadedFarms,
  title,
  onClose,
}) => {
  const useBackend = !preloadedFarms && !!graphId && !!filterValue;
  const { data, isLoading, error } = useDrillDownData(graphId || "", filterValue || "", {
    enabled: useBackend && open,
  });
  const [selectedFarmId, setSelectedFarmId] = useState<string | null>(null);
  const [farmDetailsOpen, setFarmDetailsOpen] = useState(false);

  // Report-from-anywhere state (only rendered for users with CREATE_REPORT)
  const { hasPrivilege: canCreateReport } = useHasPrivilege(PRIVILEGES.CREATE_REPORT);
  const selection = useReportOrgSelection();
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  const displayFarms = useMemo(() => preloadedFarms ?? data?.farms ?? [], [preloadedFarms, data?.farms]);
  const headerTitle = title ?? (filterValue ? `Farms: ${formatDrillDownFilterTitle(filterValue)}` : "Farms");

  // Reset report selection when the dialog closes
  const clearSelection = selection.clearSelection;
  useEffect(() => {
    if (!open) {
      clearSelection();
    }
  }, [open, clearSelection]);

  const { isSelected, selectedCount, selectMultiple, unselectMultiple, toggleSelection } =
    selection;

  const allVisibleSelected = useMemo(
    () => displayFarms.length > 0 && displayFarms.every((farm) => isSelected(farm.farmId)),
    [displayFarms, isSelected]
  );
  const someVisibleSelected = useMemo(
    () =>
      displayFarms.length > 0 &&
      !allVisibleSelected &&
      displayFarms.some((farm) => isSelected(farm.farmId)),
    [displayFarms, allVisibleSelected, isSelected]
  );

  const handleSelectAllVisible = (checked: boolean) => {
    const visible = displayFarms.map((farm) => ({ id: farm.farmId, name: farm.farmName }));
    if (checked) {
      selectMultiple(visible);
    } else {
      unselectMultiple(visible.map((org) => org.id));
    }
  };

  const handleFarmClick = (farmId: string) => {
    setSelectedFarmId(farmId);
    setFarmDetailsOpen(true);
  };

  const handleFarmDetailsClose = () => {
    setFarmDetailsOpen(false);
    setSelectedFarmId(null);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6">{headerTitle}</Typography>
            <Button onClick={onClose} startIcon={<CloseIcon />}>
              Close
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent>
          {useBackend && isLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {useBackend && error && (
            <Box sx={{ py: 4 }}>
              <Typography color="error">Error loading farms: {(error as Error).message}</Typography>
            </Box>
          )}

          {(!useBackend || (!isLoading && !error)) && (
            <>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {displayFarms.length} farm{displayFarms.length !== 1 ? "s" : ""} found
              </Typography>

              {canCreateReport && displayFarms.length > 0 && (
                <Box
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    mx: -3,
                    px: 3,
                    py: 1,
                    mb: 1,
                    bgcolor: "background.paper",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                      checked={allVisibleSelected}
                      indeterminate={someVisibleSelected}
                      onChange={(e) => handleSelectAllVisible(e.target.checked)}
                      size="small"
                      inputProps={{ "aria-label": "Select all farms" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {selectedCount > 0 ? `${selectedCount} selected` : "Select all"}
                    </Typography>
                  </Box>
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setReportDialogOpen(true)}
                    disabled={selectedCount === 0}
                  >
                    Download Report
                  </Button>
                </Box>
              )}

              {displayFarms.length === 0 ? (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <Typography color="textSecondary">No farms found</Typography>
                </Box>
              ) : (
                <List>
                  {displayFarms.map((farm) => (
                    <ListItemButton
                      key={farm.farmId}
                      onClick={() => handleFarmClick(farm.farmId)}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        mb: 1,
                      }}
                    >
                      {canCreateReport && (
                        <ListItemIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSelection({ id: farm.farmId, name: farm.farmName });
                          }}
                        >
                          <Checkbox
                            edge="start"
                            checked={isSelected(farm.farmId)}
                            tabIndex={-1}
                            disableRipple
                            inputProps={{ "aria-label": `Select ${farm.farmName}` }}
                          />
                        </ListItemIcon>
                      )}
                      <ListItemText primary={farm.farmName} secondary={farm.value} />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {canCreateReport && (
        <ReportRequestDialog
          open={reportDialogOpen}
          onClose={() => setReportDialogOpen(false)}
          orgs={selection.selectedOrgs}
          sourceLabel={headerTitle}
        />
      )}
      {/* Farm Details Modal */}
      {selectedFarmId && (
        <FarmDetailsModal
          open={farmDetailsOpen}
          onClose={handleFarmDetailsClose}
          organizationId={selectedFarmId}
        />
      )}
    </>
  );
};
